import { LocalDate, YearMonth, DateRange } from './types';
import { addDays, daysBetween, monthOf, lastDayOfMonth } from './dateHelpers';

type CellState = 'outside-month' | 'range-single' | 'range-start' | 'range-end' | 'in-range' | 'default';

const weekdayOf = (d: LocalDate): number => new Date(`${d}T00:00:00Z`).getUTCDay(); // 0 = domingo

// Semana empezando en domingo, 5 o 6 filas según cómo caiga el mes, con
// relleno de días fuera de mes al principio y al final — §07.
const generateMonthGrid = (panelMonth: YearMonth): LocalDate[][] => {
  const firstOfMonth = `${panelMonth}-01` as LocalDate;
  const gridStart = addDays(firstOfMonth, -weekdayOf(firstOfMonth));
  const lastOfMonth = lastDayOfMonth(panelMonth);
  const gridEnd = addDays(lastOfMonth, 6 - weekdayOf(lastOfMonth));

  // gridStart y gridEnd están los dos alineados a semana completa (domingo→sábado),
  // así que el tramo entre ambos es siempre múltiplo de 7 — 5 o 6 filas.
  const totalDays = daysBetween(gridStart, gridEnd) + 1;
  const days: LocalDate[] = [];
  for(let i = 0; i < totalDays; i++) {
    days.push(addDays(gridStart, i));
  }

  const weeks: LocalDate[][] = [];
  for(let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
};

// Gana la primera coincidencia — outside-month manda incluso si ese día cae
// dentro del rango: la celda de relleno no es interactiva ni pinta selección,
// eso lo comunica la tira de continuación (Fase 4).
const resolveCellState = (day: LocalDate, panelMonth: YearMonth, range: DateRange): CellState => {
  if(monthOf(day) !== panelMonth) {
    return 'outside-month';
  }
  const isStart = day === range.start;
  const isEnd = day === range.end;
  if(isStart && isEnd) {
    return 'range-single';
  }
  if(isStart) {
    return 'range-start';
  }
  if(isEnd) {
    return 'range-end';
  }
  if(day > range.start && day < range.end) {
    return 'in-range';
  }
  return 'default';
};

type NavKey = 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown' | 'Home' | 'End';

// Día destino de una tecla de navegación — pura, no decide si cruza de mes;
// eso lo compara el caller contra panelMonth (§10: una flecha que sale del
// mes navega en vez de aterrizar en una celda deshabilitada).
const resolveKeyboardTarget = (day: LocalDate, key: NavKey): LocalDate => {
  switch(key) {
    case 'ArrowLeft':
      return addDays(day, -1);
    case 'ArrowRight':
      return addDays(day, 1);
    case 'ArrowUp':
      return addDays(day, -7);
    case 'ArrowDown':
      return addDays(day, 7);
    case 'Home':
      return addDays(day, -weekdayOf(day));
    case 'End':
      return addDays(day, 6 - weekdayOf(day));
  }
};

export { CellState, NavKey, generateMonthGrid, resolveCellState, resolveKeyboardTarget };
